import { businessDetailQueryOptions } from '@/api/queries/businesses';
import { ScreenHeader } from '@/components/layout/screen-header';
import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { useAppTheme } from '@/providers/theme-provider';
import { useQuery } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAdminApproveBusiness, useAdminUpdateBusiness } from '../hooks/use-admin-businesses';

const CUISINE_TYPES = [
  'american', 'italian', 'mexican', 'chinese', 'japanese', 'thai', 'indian',
  'french', 'mediterranean', 'korean', 'vietnamese', 'greek', 'spanish',
  'middle_eastern', 'bbq', 'seafood', 'steakhouse', 'pizza', 'fast_food',
  'cafe', 'bakery', 'desserts', 'vegan', 'vegetarian', 'other',
];

const PRICE_LEVELS = ['budget', 'moderate', 'expensive', 'luxury'] as const;

export function AdminEditBusinessScreen() {
  const theme = useAppTheme();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const { data: business, isLoading } = useQuery(businessDetailQueryOptions(id!));

  const updateMutation = useAdminUpdateBusiness();
  const approveMutation = useAdminApproveBusiness();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [cuisineType, setCuisineType] = useState('');
  const [priceLevel, setPriceLevel] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');

  useEffect(() => {
    if (business) {
      setName(business.name);
      setDescription(business.description ?? '');
      setAddress(business.address ?? '');
      setStreet(business.street ?? '');
      setCity(business.city ?? '');
      setState(business.state ?? '');
      setCountry(business.country ?? '');
      setPostalCode(business.postalCode ?? '');
      setPhone(business.phone ?? '');
      setEmail(business.email ?? '');
      setWebsite(business.website ?? '');
      setCuisineType(business.cuisineType ?? '');
      setPriceLevel(business.priceLevel ?? '');
      setCategory(business.category ?? '');
      setTags(business.tags?.join(', ') ?? '');
    }
  }, [business]);

  const buildUpdateData = () => {
    const data: Record<string, any> = {};
    if (name.trim()) data.name = name.trim();
    if (description.trim()) data.description = description.trim();
    else if (business?.description) data.description = null;
    if (address.trim()) data.address = address.trim();
    if (street.trim()) data.street = street.trim();
    if (city.trim()) data.city = city.trim();
    if (state.trim()) data.state = state.trim();
    if (country.trim()) data.country = country.trim();
    if (postalCode.trim()) data.postal_code = postalCode.trim();
    if (phone.trim()) data.phone = phone.trim();
    if (email.trim()) data.email = email.trim();
    if (website.trim()) data.website = website.trim();
    if (cuisineType) data.cuisine_type = cuisineType;
    if (priceLevel) data.price_level = priceLevel;
    if (category.trim()) data.category = category.trim();
    if (tags.trim()) data.tags = tags.trim();
    return data;
  };

  const handleSave = () => {
    if (!id || !name.trim()) return;

    updateMutation.mutate(
      { id, data: buildUpdateData() },
      {
        onSuccess: () => {
          toast.success('Business updated');
          router.back();
        },
        onError: (err) => {
          toast.error(err.message);
        },
      }
    );
  };

  const handleSaveAndApprove = () => {
    if (!id || !name.trim()) return;

    updateMutation.mutate(
      { id, data: buildUpdateData() },
      {
        onSuccess: () => {
          approveMutation.mutate(id, {
            onSuccess: () => {
              toast.success('Business approved');
              router.back();
            },
            onError: (err) => {
              toast.error('Saved but failed to approve', { description: err.message });
            },
          });
        },
        onError: (err) => {
          toast.error(err.message);
        },
      }
    );
  };

  const isSaving = updateMutation.isPending || approveMutation.isPending;

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 bg-background">
        <ScreenHeader title="Edit Business" showBackButton />
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  if (!business) {
    return (
      <SafeAreaView className="flex-1 bg-background">
        <ScreenHeader title="Edit Business" showBackButton />
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Text className="text-muted-foreground">Business not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-background">
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="px-4 pb-8"
      >
        <ScreenHeader title="Edit Business" showBackButton />

        <TextInput label="Name" containerClassName="mb-4" value={name} onChangeText={setName} placeholder="Business name" />

        <TextInput label="Description" containerClassName="mb-4" value={description} onChangeText={setDescription} placeholder="Description (optional)" multiline numberOfLines={3} />

        <Text accessibilityRole="header" className="text-foreground" style={{ fontSize: 15, fontWeight: '700', marginBottom: 8 }}>Location</Text>

        <TextInput label="Address" containerClassName="mb-3" value={address} onChangeText={setAddress} placeholder="Address" />

        <TextInput label="Street" containerClassName="mb-3" value={street} onChangeText={setStreet} placeholder="Street" />

        <View style={{ flexDirection: 'row', gap: 12, marginBottom: 12 }}>
          <TextInput label="City" containerClassName="flex-1" value={city} onChangeText={setCity} placeholder="City" />
          <TextInput label="State" containerClassName="flex-1" value={state} onChangeText={setState} placeholder="State" />
        </View>

        <View style={{ flexDirection: 'row', gap: 12, marginBottom: 16 }}>
          <TextInput label="Country" containerClassName="flex-1" value={country} onChangeText={setCountry} placeholder="Country" />
          <TextInput label="Postal Code" containerClassName="flex-1" value={postalCode} onChangeText={setPostalCode} placeholder="Postal Code" />
        </View>

        <Text accessibilityRole="header" className="text-foreground" style={{ fontSize: 15, fontWeight: '700', marginBottom: 8 }}>Contact</Text>

        <TextInput label="Phone" containerClassName="mb-3" value={phone} onChangeText={setPhone} placeholder="Phone" keyboardType="phone-pad" />

        <TextInput label="Email" containerClassName="mb-3" value={email} onChangeText={setEmail} placeholder="Email" keyboardType="email-address" autoCapitalize="none" />

        <TextInput label="Website" containerClassName="mb-4" value={website} onChangeText={setWebsite} placeholder="Website" keyboardType="url" autoCapitalize="none" />

        <Text accessibilityRole="header" className="text-foreground" style={{ fontSize: 15, fontWeight: '700', marginBottom: 8 }}>Classification</Text>

        <Text className="text-muted-foreground" style={{ fontSize: 13, fontWeight: '600', marginBottom: 6, paddingHorizontal: 4 }}>Cuisine Type</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {CUISINE_TYPES.map((type) => (
              <Pressable
                key={type}
                onPress={() => setCuisineType(cuisineType === type ? '' : type)}
                accessibilityRole="button"
                accessibilityLabel={type.replace(/_/g, ' ')}
                accessibilityState={{ selected: cuisineType === type }}
                className={cuisineType === type ? 'bg-primary border-primary' : 'bg-surface border-border'}
                style={{
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  borderRadius: 16,
                  borderWidth: 1,
                }}
              >
                <Text style={{ fontSize: 12, color: cuisineType === type ? '#fff' : undefined }} className={cuisineType === type ? undefined : 'text-foreground'}>
                  {type.replace(/_/g, ' ')}
                </Text>
              </Pressable>
            ))}
          </View>
        </ScrollView>

        <Text className="text-muted-foreground" style={{ fontSize: 13, fontWeight: '600', marginBottom: 6, paddingHorizontal: 4 }}>Price Level</Text>
        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
          {PRICE_LEVELS.map((level) => (
            <Pressable
              key={level}
              onPress={() => setPriceLevel(priceLevel === level ? '' : level)}
              accessibilityRole="button"
              accessibilityLabel={`Price level ${level}`}
              accessibilityState={{ selected: priceLevel === level }}
              className={priceLevel === level ? 'bg-primary border-primary' : 'bg-surface border-border'}
              style={{
                flex: 1,
                paddingVertical: 8,
                borderRadius: 8,
                alignItems: 'center',
                borderWidth: 1,
              }}
            >
              <Text style={{ fontSize: 13, fontWeight: '600', color: priceLevel === level ? '#fff' : undefined }} className={priceLevel === level ? undefined : 'text-foreground'}>
                {level}
              </Text>
            </Pressable>
          ))}
        </View>

        <TextInput label="Category" containerClassName="mb-3" value={category} onChangeText={setCategory} placeholder="Category" />

        <TextInput label="Tags (comma-separated)" containerClassName="mb-6" value={tags} onChangeText={setTags} placeholder="e.g. wifi, outdoor-seating, dog-friendly" />

        <Pressable
          onPress={handleSave}
          disabled={isSaving || !name.trim()}
          className="bg-surface-secondary"
          style={{
            borderRadius: 10,
            paddingVertical: 14,
            alignItems: 'center',
            marginBottom: 10,
            opacity: isSaving || !name.trim() ? 0.5 : 1,
          }}
        >
          <Text className="text-foreground" style={{ fontSize: 16, fontWeight: '600' }}>
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Text>
        </Pressable>

        {business.status === 'pending' && (
          <Pressable
            onPress={handleSaveAndApprove}
            disabled={isSaving || !name.trim()}
            className="bg-primary"
            style={{
              borderRadius: 10,
              paddingVertical: 14,
              alignItems: 'center',
              opacity: isSaving || !name.trim() ? 0.5 : 1,
            }}
          >
            <Text className="text-background" style={{ fontSize: 16, fontWeight: '600' }}>
              {approveMutation.isPending ? 'Approving...' : 'Save & Approve'}
            </Text>
          </Pressable>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
